import Vue from 'vue';
import { MESSAGE_TYPE } from 'widget/helpers/constants';
import { findUndeliveredMessage } from './helpers';

function findGreetingMessage(conversations, message) {
  return (
    Object.values(conversations).find(
      msg => msg.is_greeting_message && msg.source_id === message.source_id
    ) || null
  );
}

export const mutations = {
  clearConversations($state) {
    Vue.set($state, 'conversations', {});
  },
  pushMessageToConversation($state, message) {
    const { id, status, message_type: type } = message;
    const messagesInbox = $state.conversations;

    // skip checking preview messages when typebot is not enabled
    const { typebotGreetingMessages } = window?.hooryWebChannel || {};
    if (typeof typebotGreetingMessages !== 'undefined') {
      const conversationMsg = findGreetingMessage(
        $state.conversations,
        message
      );
      if (conversationMsg) {
        if (conversationMsg.content === 'bot_form') {
          const msg = {
            ...conversationMsg,
            content_type: message.content_type,
            content_attributes: {
              ...(message.content_attributes || {}),
              ...conversationMsg.content_attributes,
              message_real_id: message.id,
            },
          };
          Vue.delete(messagesInbox, conversationMsg.id);
          Vue.set(messagesInbox, conversationMsg.id, msg);
        }
        return;
      }
    }
    const isMessageIncoming = type === MESSAGE_TYPE.INCOMING;
    const isTemporaryMessage = status === 'in_progress';

    if (!isMessageIncoming || isTemporaryMessage) {
      Vue.set(messagesInbox, id, message);
      return;
    }

    const [messageInConversation] = findUndeliveredMessage(
      messagesInbox,
      message
    );
    if (!messageInConversation) {
      Vue.set(messagesInbox, id, message);
    } else {
      Vue.delete(messagesInbox, messageInConversation.id);
      Vue.set(messagesInbox, id, message);
    }
  },

  setLastMessageOnConversation($state, { conversationId, message }) {
    const conversationIndex = $state.allConversations.findIndex(
      conversation => conversation.display_id === conversationId
    );
    if (conversationIndex < 0) return;

    const conversation = $state.allConversations[conversationIndex];
    conversation.last_message = message;

    if (message.sender_type === 'User') {
      // eslint-disable-next-line no-plusplus
      conversation.unread_count++;
    }
    conversation.in_queue = message.conversation?.in_queue;

    $state.allConversations.splice(conversationIndex, 1);
    $state.allConversations.unshift(conversation);
  },
  setMessageStatusOnConversation($state, { conversationId, status }) {
    const conversationIndex = $state.allConversations.findIndex(
      conversation => conversation.display_id === conversationId
    );
    if (conversationIndex < 0) return;

    const conversation = $state.allConversations[conversationIndex];
    conversation.status = status;
  },
  readConversationMessages($state, { conversationId }) {
    const conversationIndex = $state.allConversations.findIndex(
      conversation => conversation.display_id === conversationId
    );
    if (conversationIndex < 0) return;

    const conversation = $state.allConversations[conversationIndex];
    conversation.unread_count = 0;
  },
  setConversationQueueStatus($state, { conversationId, message }) {
    const conversationIndex = $state.allConversations.findIndex(
      conversation => conversation.display_id === conversationId
    );
    if (conversationIndex < 0) return;

    const conversation = $state.allConversations[conversationIndex];
    if (typeof message.conversation?.in_queue !== 'undefined') {
      conversation.in_queue = message.conversation.in_queue;
    }
  },
  updateAttachmentMessageStatus($state, { message, tempId }) {
    const { id } = message;
    const messagesInbox = $state.conversations;

    const messageInConversation = messagesInbox[tempId];

    if (messageInConversation) {
      Vue.delete(messagesInbox, tempId);
      Vue.set(messagesInbox, id, { ...message });
    }
  },

  setConversationUIFlag($state, uiFlags) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...uiFlags,
    };
  },

  setConversationListLoading($state, status) {
    $state.uiFlags.isFetchingList = status;
  },
  setIsFetchingMessages($state, status) {
    $state.uiFlags.isFetchingMessages = status;
  },

  setMessagesInConversation($state, payload) {
    if (!payload.length) {
      $state.uiFlags.allMessagesLoaded = true;
      return;
    }

    payload.map(message => Vue.set($state.conversations, message.id, message));
  },

  setMissingMessagesInConversation($state, payload) {
    Vue.set($state, 'conversations', payload);
  },

  updateMessage($state, { id, content_attributes }) {
    $state.conversations[id] = {
      ...$state.conversations[id],
      content_attributes: {
        ...($state.conversations[id].content_attributes || {}),
        ...content_attributes,
      },
    };
  },

  updateMessageMeta($state, { id, meta }) {
    const message = $state.conversations[id];
    if (!message) return;

    const newMeta = message.meta ? { ...message.meta, ...meta } : { ...meta };
    Vue.set(message, 'meta', {
      ...newMeta,
    });
  },

  deleteMessage($state, id) {
    const messagesInbox = $state.conversations;
    Vue.delete(messagesInbox, id);
  },

  toggleAgentTypingStatus($state, { status }) {
    $state.uiFlags.isAgentTyping = status === 'on';
  },
  toggleAgentTypingStatusList($state, { conversationId, status }) {
    const conversationIndex = $state.allConversations.findIndex(
      conversation => conversation.display_id === conversationId
    );
    if (conversationIndex < 0) return;

    $state.allConversations[conversationIndex].is_agent_typing =
      status === 'on';
  },
  changeConversationStatusList($state, { conversationId, status }) {
    const conversationIndex = $state.allConversations.findIndex(
      conversation => conversation.display_id === conversationId
    );
    if (conversationIndex < 0) return;

    $state.allConversations[conversationIndex].status = status;
  },

  setMetaUserLastSeenAt($state, lastSeen) {
    $state.meta.userLastSeenAt = lastSeen;
  },
  setAllConversations($state, data) {
    $state.allConversations = data;
  },

  setLastMessageId($state) {
    const { conversations } = $state;
    const lastMessage = Object.values(conversations).pop();
    if (!lastMessage) return;
    const { id } = lastMessage;
    $state.lastMessageId = id;
  },
};
